import React, { useEffect, useState } from 'react';
import { Button, Container, Group, Loader, Text, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconEye } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import axios from 'axios';
import { config } from '../helpers/Config';
import { getAccessToken } from '../helpers/TokenRefresh';
import { Receipt } from '../types/receipt.type';

const ReceiptEdit: React.FC<{ id?: number }> = ({ id }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [documentDate, setDocumentDate] = useState<Date | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;
    getAccessToken().then(token =>
      axios
        .get(`${config.apiUrl}/receipt/${id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          const data: Receipt = res.data.data;
          setReceipt(data);
          setDocumentDate(data.documentDate ? new Date(data.documentDate) : null);
        })
        .catch(() => notifications.show({ message: 'Beleg nicht gefunden', color: 'red' }))
        .finally(() => setLoading(false))
    );
  }, [id]);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const token = await getAccessToken();
      const d = documentDate instanceof Date ? documentDate : (documentDate ? new Date(documentDate as any) : null);
      await axios.patch(
        `${config.apiUrl}/receipt/${id}`,
        { documentDate: d && !isNaN(d.getTime()) ? d.toISOString() : null },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
      );
      notifications.show({ message: 'Gespeichert', color: 'green' });
      navigate(-1);
    } catch {
      notifications.show({ message: 'Speichern fehlgeschlagen', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!id) return;
    const token = await getAccessToken();
    fetch(`${config.apiUrl}/receipt/${id}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const win = window.open(blobUrl, '_blank');
        win?.addEventListener('load', () => URL.revokeObjectURL(blobUrl), { once: true });
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      })
      .catch(() => notifications.show({ message: 'Vorschau fehlgeschlagen', color: 'red' }));
  };

  if (loading) return <Loader size="md" mt="xl" ml="xl" />;
  if (!receipt) return <Text mt="xl" ml="xl">Beleg nicht gefunden.</Text>;

  return (
    <Container size="sm" mt="xl">
      <Title order={2} mb="md">Beleg bearbeiten</Title>

      <Group mb="xs">
        <Text fw={600} w={140}>Dateiname</Text>
        <Text>{receipt.fileName}</Text>
      </Group>
      <Group mb="xs">
        <Text fw={600} w={140}>Person</Text>
        <Text>{receipt.personName || '—'}</Text>
      </Group>
      <Group mb="xs">
        <Text fw={600} w={140}>Buchungsdatum</Text>
        <Text>{receipt.bookingDate ? dayjs(receipt.bookingDate).format('DD.MM.YYYY') : '—'}</Text>
      </Group>
      <Group mb="xs">
        <Text fw={600} w={140}>Buchungstext</Text>
        <Text>{receipt.bookingText || '—'}</Text>
      </Group>
      <Group mb="xs">
        <Text fw={600} w={140}>Hochgeladen</Text>
        <Text>{dayjs(receipt.createdAt).format('DD.MM.YYYY HH:mm')}</Text>
      </Group>

      <DatePickerInput
        clearable
        valueFormat="DD.MM.YYYY"
        label="Belegdatum"
        value={documentDate}
        onChange={(v: any) => setDocumentDate(v ? (v instanceof Date ? v : new Date(v)) : null)}
        mt="md"
        mb="lg"
      />

      <Group>
        <Button onClick={handleSave} loading={saving}>Speichern</Button>
        <Button leftSection={<IconEye size={16} />} variant="light" onClick={handlePreview}>
          Vorschau
        </Button>
        <Button variant="subtle" onClick={() => navigate(-1)}>Abbrechen</Button>
      </Group>
    </Container>
  );
};

export default ReceiptEdit;
